import { useInterval } from "core/hooks";
import api from "core/model/api";
import { Session } from "core/model/session";
import {
  activateLoggingTracking,
  isCI,
  isTest,
} from "core/model/utils/featureFlags";
import { BaseEventSchema, PlatformInfo } from "core/validationSchemas/tracking";
import { format } from "date-fns";
import { useState } from "react";
import { useTracking } from "react-tracking";
import { APP_ACP, APP_PROVIDER_SEARCH } from "../../consts";
import { AppType } from "../../types";
import { mockedAnalytics } from "../api/endpoints/analytics/mock";
import { getProviderSearchFromPathname } from "../providerSearch";

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    batchedEvents: BaseEventSchema[];
  }
}

const { track } = activateLoggingTracking ? api.analytics : mockedAnalytics;

export default function useTrackingProvider({
  account_id,
  app,
  careprovider_id,
  careprovider_name,
  careseeker_name,
  first_name,
  last_name,
  platformInfo,
  session,
}: {
  account_id?: number | null;
  app: AppType;
  careprovider_id?: number | null;
  careprovider_name?: string;
  careseeker_name?: string;
  first_name?: string;
  last_name?: string;
  platformInfo: PlatformInfo;
  session: Session;
}) {
  const [batchedEvents, setBatchedEvents] = useState<BaseEventSchema[]>([]);

  const { Track } = useTracking<BaseEventSchema>(
    {
      platform_information: {
        application: platformInfo.application,
        browser: platformInfo.browser,
        browser_version: platformInfo.browser_version,
        build: platformInfo.build,
        device_id: platformInfo.device_id,
        device_screen_height: platformInfo.device_screen_height,
        device_screen_width: platformInfo.device_screen_width,
        env: platformInfo.env,
        hostname: platformInfo.hostname,
        is_pwa: platformInfo.is_pwa,
        model: platformInfo.model,
        operation_system: platformInfo.operation_system,
        operation_system_version: platformInfo.operation_system_version,
        platform: platformInfo.platform,
        ...(app === APP_PROVIDER_SEARCH && {
          provider_search_state: getProviderSearchFromPathname(),
        }),
        version: platformInfo.version,
      },
      session_information: {
        session_id: session.id,
        session_item: session.trackingEventsCount,
      },
      timestamp: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"),
      user_information: {
        account_id: account_id || null,
        auction_id: null,
        auction_request_id: null,
        careprovider_id: careprovider_id || null,
        careprovider_name: careprovider_name || null,
        careseeker_id: null,
        careseeker_name: careseeker_name || null,
        first_name: first_name || null,
        last_name: last_name || null,
        patient_id: null,
      },
    },
    {
      dispatch(data: BaseEventSchema) {
        if (app === APP_ACP) return;
        if (isTest) {
          window.batchedEvents = [...(window.batchedEvents || []), data];
        } else {
          setBatchedEvents((prev) => [...prev, data]);
        }

        if (!data.system_event) {
          // Monitor why sessions get extended at night.
          const currentDate = new Date();
          if (currentDate.getHours() < 3) {
            console.info(`Off-hours session extension: ${data.name}`);
          }
          if (session.hasExpired()) {
            session.reset();
          } else {
            session.moveExpiryAhead();
          }
        }
        // Update session
        session.incrementTrackingEventsCount();
      },
    },
  );

  useInterval(
    () => {
      const batch = isTest ? window.batchedEvents : batchedEvents;
      if (batch?.length) {
        track(batch);

        const updatedBatchedEvents: BaseEventSchema[] = [];
        if (isTest) {
          window.batchedEvents = updatedBatchedEvents;
        } else {
          setBatchedEvents(updatedBatchedEvents);
        }
      }
    },
    isCI ? 1000 : 5000,
  );

  return { Track };
}
